import { useCallback, useEffect, useState } from 'react'

import type { GTMEcommerceItem } from '@/helpers/types/gtmTypes'

import { useWatchlistToggler } from './useWatchlistToggler'

type UseToggleWatchlistingProps = {
  listingId: string
  dateExpires: string
  isWatchlisted: boolean
  gtmItem?: GTMEcommerceItem
  currency?: string
  listingEndTime?: string
  storefrontName?: string
  // Called after a successful undo-restore, so the caller can reverse any
  // page-local state it applied on removal (e.g. re-add the listing to a
  // search allowlist). Only meaningful on the delete path; ignored on add.
  onUndo?: () => void
}

type UseToggleWatchlisting = {
  isCurrentlyWatchlisted: boolean
  isPending: boolean
  // Resolves to `true` when the mutation succeeded and `false` when it was
  // caught and turned into an error toast. Callers that want to gate follow-up
  // state changes on success (e.g. optimistically hiding a card) should check
  // the return value; callers that only care about the toast can ignore it.
  toggleWatchlistListing: () => Promise<boolean>
}

/**
 * The watchlist toggle for a surface showing **one** listing: the mutations,
 * toasts, GTM and undo of {@link useWatchlistToggler}, plus the optimistic
 * `isCurrentlyWatchlisted` and `isPending` state a single control needs.
 *
 * A surface toggling *many* listings reaches for `useWatchlistToggler`
 * directly instead and holds its own per-listing state — for performance, not
 * tidiness. One instance of this hook per listing means one `useFlag`, and so
 * one LaunchDarkly subscription, per listing; across a List View's ~40 visible
 * rows that is what the action panel's load-time budget cannot absorb.
 */
const useToggleWatchlisting = ({
  listingId,
  dateExpires,
  isWatchlisted,
  gtmItem,
  currency,
  listingEndTime,
  storefrontName,
  onUndo,
}: UseToggleWatchlistingProps): UseToggleWatchlisting => {
  const [isPending, setIsPending] = useState(false)
  const [isCurrentlyWatchlisted, setIsCurrentlyWatchlisted] =
    useState<boolean>(isWatchlisted)

  useEffect(() => {
    setIsCurrentlyWatchlisted(isWatchlisted) // oxlint-disable-line react/set-state-in-effect
  }, [isWatchlisted])

  const { isSignedIn, toggleWatchlisting } = useWatchlistToggler()

  const toggleWatchlistListing = useCallback(async (): Promise<boolean> => {
    const target = {
      listingId,
      dateExpires,
      isWatchlisted: isCurrentlyWatchlisted,
      gtmItem,
      currency,
      listingEndTime,
      storefrontName,
      // A restored listing is watched again, whatever it was before.
      onUndo: () => {
        setIsCurrentlyWatchlisted(true)
        onUndo?.()
      },
    }

    // A signed-out buyer is redirected, not made to wait: entering the pending
    // state for a click that never mutates would disable the control on the way
    // out.
    if (!isSignedIn) return toggleWatchlisting(target)

    setIsPending(true)

    try {
      const didSucceed = await toggleWatchlisting(target)

      if (didSucceed) setIsCurrentlyWatchlisted((prev) => !prev)

      return didSucceed
    } finally {
      setIsPending(false)
    }
  }, [
    currency,
    dateExpires,
    gtmItem,
    isSignedIn,
    isCurrentlyWatchlisted,
    listingEndTime,
    listingId,
    onUndo,
    storefrontName,
    toggleWatchlisting,
  ])

  return {
    isCurrentlyWatchlisted,
    isPending,
    toggleWatchlistListing,
  }
}

export default useToggleWatchlisting
